import React, { useCallback, useEffect, useState } from 'react';
import './EbayScraper.css';
import API_BASE_URL from '../config/apiBaseUrl';

const getAuthHeaders = (headers = {}) => {
  const token = localStorage.getItem('authToken');
  return token ? { ...headers, Authorization: `Bearer ${token}` } : headers;
};

const formatCurrency = (value) => `${Number(value || 0).toFixed(2)} EUR`;

function EbayTopSales() {
  const [sellers, setSellers] = useState([]);
  const [selectedSeller, setSelectedSeller] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [salesApiAvailable, setSalesApiAvailable] = useState(true);
  const [topSales, setTopSales] = useState({ occasion: [], remanufactured: [] });
  const [missingStockLibelles, setMissingStockLibelles] = useState([]);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const loadSellers = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/ebay/sellers`, {
          headers: getAuthHeaders(),
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || 'Erreur chargement vendeurs');
        }
        setSellers(data.sellers || []);
      } catch (err) {
        setError(err.message);
      }
    };

    loadSellers();
  }, []);

  const loadData = useCallback(async (seller = selectedSeller) => {
    setLoading(true);
    setError(null);
    setSalesApiAvailable(true);

    try {
      const params = new URLSearchParams();
      params.set('limit', '50');
      if (seller) {
        params.set('seller', seller);
      }

      const occasionParams = new URLSearchParams(params);
      occasionParams.set('condition', 'Occasion');
      const remanufacturedParams = new URLSearchParams();
      remanufacturedParams.set('limit', '50');
      remanufacturedParams.set('condition', 'Remanufacturé');

      const [occasionResponse, remanufacturedResponse, missingResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/ebay/sales/top?${occasionParams.toString()}`, {
          headers: getAuthHeaders(),
        }),
        fetch(`${API_BASE_URL}/ebay/sales/top?${remanufacturedParams.toString()}`, {
          headers: getAuthHeaders(),
        }),
        fetch(`${API_BASE_URL}/ebay/sales/missing-stock-libelles?${params.toString()}`, {
          headers: getAuthHeaders(),
        }),
      ]);

      const occasionData = await occasionResponse.json();
      const remanufacturedData = await remanufacturedResponse.json();
      const missingData = await missingResponse.json();

      if (!occasionResponse.ok || !remanufacturedResponse.ok) {
        if (occasionResponse.status === 404 || remanufacturedResponse.status === 404) {
          setSalesApiAvailable(false);
          throw new Error('Les routes /api/ebay/sales/* ne sont pas disponibles sur le backend deploye.');
        }
        throw new Error(occasionData.error || remanufacturedData.error || 'Erreur chargement top ventes');
      }
      if (!missingResponse.ok) {
        if (missingResponse.status === 404) {
          setSalesApiAvailable(false);
          throw new Error('Les routes /api/ebay/sales/* ne sont pas disponibles sur le backend deploye.');
        }
        throw new Error(missingData.error || 'Erreur chargement libellés manquants');
      }

      setTopSales({
        occasion: occasionData.data || [],
        remanufactured: remanufacturedData.data || [],
      });
      setMissingStockLibelles(missingData.data || []);
    } catch (err) {
      setError(err.message);
      setTopSales({ occasion: [], remanufactured: [] });
      setMissingStockLibelles([]);
    } finally {
      setLoading(false);
    }
  }, [selectedSeller]);

  useEffect(() => {
    loadData('');
  }, [loadData]);

  const handleExport = async () => {
    setExporting(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.set('limit', '50');
      if (selectedSeller) {
        params.set('seller', selectedSeller);
      }

      const response = await fetch(`${API_BASE_URL}/ebay/sales/export?${params.toString()}`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 404) {
          setSalesApiAvailable(false);
          throw new Error('Export indisponible: le backend deploye ne contient pas encore /api/ebay/sales/export.');
        }
        throw new Error('Erreur lors de l\'export Excel');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ebay_top_ventes_oem_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="ebay-scraper">
      <div className="scraper-header">
        <h1>Top 50</h1>
        <p>Classement basé sur les RefOEM les plus vendues</p>
      </div>

      <div className="scraper-container">
        <div className="analytics-header">
          <div>
            <h2>Top ventes par RefOEM</h2>
            <p>Le classement principal ne prend pas en compte les libellés.</p>
          </div>

          <div className="analytics-actions">
            <select
              className="input-field analytics-filter"
              value={selectedSeller}
              onChange={(e) => setSelectedSeller(e.target.value)}
              disabled={loading || !salesApiAvailable}
            >
              <option value="">Tous les vendeurs</option>
              {sellers.map((seller) => (
                <option key={seller.id || seller.name} value={seller.name}>
                  {seller.label || seller.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="secondary-btn"
              onClick={() => loadData(selectedSeller)}
              disabled={loading || !salesApiAvailable}
            >
              {loading ? 'Chargement...' : 'Actualiser'}
            </button>
            <button
              type="button"
              className="secondary-btn accent"
              onClick={handleExport}
              disabled={loading || exporting || !salesApiAvailable}
            >
              {exporting ? 'Export...' : 'Exporter Excel'}
            </button>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <strong>Erreur:</strong> {error}
          </div>
        )}

        {!salesApiAvailable && (
          <div className="error-message">
            <strong>Action requise:</strong> redeployer le backend Symfony avec les routes
            {' '}`/api/ebay/sales/top`, `/api/ebay/sales/missing-stock-libelles` et `/api/ebay/sales/export`.
          </div>
        )}

        <div className="analytics-summary">
          <div className="stat">
            <span className="stat-label">Top Occasion</span>
            <span className="stat-value">{topSales.occasion.length}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Top Remanufacturé</span>
            <span className="stat-value">{topSales.remanufactured.length}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Libellés absents de stocks</span>
            <span className="stat-value">{missingStockLibelles.length}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Filtre vendeur</span>
            <span className="stat-value analytics-seller-value">{selectedSeller || 'Tous les vendeurs'}</span>
          </div>
        </div>

        <div className="analytics-grid">
          <div className="analytics-card">
            <h3>Top 50 RefOEM — Occasion</h3>
            {topSales.occasion.length === 0 ? (
              <p className="empty-inline">Aucune vente estimée disponible.</p>
            ) : (
              <div className="analytics-table-wrapper">
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>RefOEM</th>
                      <th>Ventes</th>
                      <th>Vendeurs</th>
                      <th>Prix moyen</th>
                      <th>Prix min</th>
                      <th>Prix max</th>
                      <th>Délai moyen (j)</th>
                      <th>Dernière vente</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topSales.occasion.map((row) => (
                      <tr key={`${row.rank}-${row.oem_reference}`}>
                        <td>{row.rank}</td>
                        <td>{row.oem_reference}</td>
                        <td>{row.sold_count}</td>
                        <td>{row.seller_count}</td>
                        <td>{formatCurrency(row.average_price)}</td>
                        <td>{formatCurrency(row.min_price)}</td>
                        <td>{formatCurrency(row.max_price)}</td>
                        <td>{row.avg_days_to_sell != null ? row.avg_days_to_sell : '-'}</td>
                        <td>{row.last_sold_date || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="analytics-card">
            <h3>Top 50 RefOEM — Remanufacturé (global)</h3>
            {topSales.remanufactured.length === 0 ? (
              <p className="empty-inline">Aucune vente estimée disponible.</p>
            ) : (
              <div className="analytics-table-wrapper">
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>#</th><th>RefOEM</th><th>Ventes</th><th>Vendeurs</th><th>Prix moyen</th>
                      <th>Prix min</th><th>Prix max</th><th>Délai moyen (j)</th><th>Dernière vente</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topSales.remanufactured.map((row) => (
                      <tr key={`${row.rank}-${row.oem_reference}`}>
                        <td>{row.rank}</td><td>{row.oem_reference}</td><td>{row.sold_count}</td>
                        <td>{row.seller_count}</td><td>{formatCurrency(row.average_price)}</td>
                        <td>{formatCurrency(row.min_price)}</td><td>{formatCurrency(row.max_price)}</td>
                        <td>{row.avg_days_to_sell != null ? row.avg_days_to_sell : '-'}</td>
                        <td>{row.last_sold_date || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="analytics-card">
            <h3>Libellés vendus absents de stocks</h3>
            {missingStockLibelles.length === 0 ? (
              <p className="empty-inline">Aucun libellé manquant détecté.</p>
            ) : (
              <div className="missing-libelles-list">
                {missingStockLibelles.map((row) => (
                  <div className="missing-libelle-item" key={row.libelle}>
                    <div>
                      <strong>{row.libelle}</strong>
                      <span>{row.sold_count} vente(s) estimée(s) chez {row.seller_count} vendeur(s)</span>
                    </div>
                    <small>{row.last_sold_date || 'Date inconnue'}</small>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EbayTopSales;
